/**
 * Asset source definitions
 *
 * Source string constants and metadata for property/asset data
 * (KPIs, time series, business plan, unit type charts, breakdowns list).
 *
 * Each constant value matches the corresponding DataSource enum member so
 * both can be used interchangeably. The DataSource enum is kept in
 * data-source-types.ts for use in switch/case statements and type utilities;
 * AssetSources is the canonical runtime string form used in canvas configs.
 *
 * Source metadata is registered into the shared canvas infrastructure at
 * module-load time so the data-source analyzer can determine which query
 * groups to enable for a given canvas config.
 */

import { registerSourceMetadata } from "~/helpers/canvas/data-source-types";

export const AssetSources = {
  // Asset KPIs - from AssetBreakdownAt query
  TOTAL_UNITS: "asset.totalUnits",
  ANNUAL_RENT: "asset.annualRent",
  LEASABLE_AREA: "asset.leasableArea",
  RENT_PER_SQM: "asset.rentPerSqm",
  VACANCY_RATE_KPI: "asset.vacancyRateKpi",
  ECONOMIC_VACANCY_RATE_KPI: "asset.economicVacancyRateKpi",
  AREA_VACANCY_RATE_KPI: "asset.areaVacancyRateKpi",
  CHURN_ROLLING_12M_KPI: "asset.churnRolling12mKpi",

  // Asset Business Plan - from PropertyBusinessPlan query
  BUSINESS_PLAN_PRICE: "asset.businessPlanPrice",
  BUSINESS_PLAN_PRICE_SQM: "asset.businessPlanPriceSqm",
  ALLOCATED_PURCHASE_PRICE: "asset.allocatedPurchasePrice",
  APP_COST: "asset.appCost",

  // Asset Time Series - from AssetBreakdownBetween query
  RENT_BY_TYPE_GROUP: "asset.rentByTypeGroup",
  VACANCY_RATE: "asset.vacancyRate",
  VACANCY_RATE_BY_AREA: "asset.vacancyRateByArea",
  IDLE_TENANCY_RATE: "asset.idleTenancyRate",
  IDLE_ANNUAL_AMOUNT: "asset.idleAnnualAmount",
  RENT_PER_SQM_OVER_TIME: "asset.rentPerSqmOverTime",
  RENT_DEVELOPMENT_YOY: "asset.rentDevelopmentYoy",

  // Asset Charts - from CanvasPropertyTenancies query
  UNIT_TYPE_BY_AREA: "asset.unitTypeByArea",
  UNIT_TYPE_BY_COUNT: "asset.unitTypeByCount",

  // Asset Breakdowns List - from AssetBreakdownsAt query
  BREAKDOWNS_LIST: "asset.breakdownsList",

  // Asset Yield - from CanvasYieldQuery
  TOTAL_RETURN_OVER_TIME: "asset.totalReturnOverTime",
  // Asset Total Return % bar chart - from CanvasYieldQuery (period-over-period %)
  TOTAL_RETURN_PERCENT_OVER_TIME: "asset.totalReturnPercentOverTime",
  // Asset Total Return % Waterfall - from CanvasYieldQuery (cumulative % from static baseline)
  TOTAL_RETURN_PERCENT_WATERFALL: "asset.totalReturnPercentWaterfall",
  // Property Value (Ejendomsværdi) KPIs - from CanvasYieldQuery (QUARTERLY fetch)
  PROPERTY_VALUE_KPI: "asset.propertyValueKpi",
  PROPERTY_VALUE_LAST_QUARTER_KPI: "asset.propertyValueLastQuarterKpi",

  // Asset Operating Margin - from CanvasOperatingMarginQuery
  OPERATING_MARGIN_OVER_TIME: "asset.operatingMarginOverTime",

  // Asset NOI Over Time - from CanvasOperatingMarginQuery (absolute amount)
  NET_OPERATING_INCOME_OVER_TIME: "asset.netOperatingIncomeOverTime",

  // Asset Churn Rate - from AssetBreakdownBetween query
  CHURN_RATE: "asset.churnRate",

  // Asset Terminations (opsigelser) over time - from AssetBreakdownBetween query
  TERMINATIONS_OVER_TIME: "asset.terminationsOverTime",

  // Asset WAULT - from AssetBreakdownBetween query
  WAULT: "asset.wault",
  WAULT_KPI: "asset.waultKpi",

  // Asset Return KPIs - derived from CanvasYieldQuery data
  // Actual total return for a canvas-defined period (defaults to last completed calendar year)
  RETURN_ACTUAL_KPI: "asset.returnActualKpi",
  // Budgeted return for a canvas-defined period (defaults to current calendar year)
  RETURN_BUDGETED_KPI: "asset.returnBudgetedKpi",

  // Direct yield KPI - NOI / beginning-of-period property value (percentage)
  DIRECT_YIELD_KPI: "asset.directYieldKpi",
  // Indirect yield KPI - property value change / beginning-of-period PV (percentage)
  INDIRECT_YIELD_KPI: "asset.indirectYieldKpi",

  // Historical IRR KPI - from AssetIrr query (property value + cash flow)
  IRR_KPI: "asset.irrKpi",

  // Top tenancies table - from CanvasTopTenancies query
  TOP_TENANCIES_BY_RENT: "asset.topTenanciesByRent",

  // Unit & area KPIs - from AssetBreakdownAt query
  RENTED_UNITS_KPI: "asset.rentedUnitsKpi",
  VACANT_UNITS_KPI: "asset.vacantUnitsKpi",
  RENTED_AREA_KPI: "asset.rentedAreaKpi",
  VACANT_AREA_KPI: "asset.vacantAreaKpi",
  VACANCY_AMOUNT_KPI: "asset.vacancyAmountKpi",

  // Financial KPIs - from CanvasOperatingMarginQuery (income statements)
  RENTAL_INCOME_KPI: "asset.rentalIncomeKpi",
  NOI_MARGIN_KPI: "asset.noiMarginKpi",
  RESULTS_MARGIN_KPI: "asset.resultsMarginKpi",
  INCOME_NOMINAL_OVER_TIME: "asset.incomeNominalOverTime",

  // Income by category table - from CanvasIncomeByCategory query (income statements + budget)
  INCOME_BY_CATEGORY: "asset.incomeByCategory",

  // Rent Growth KPI - rolling 12M % change from AssetBreakdownBetween
  RENT_GROWTH_ROLLING_12M_KPI: "asset.rentGrowthRolling12mKpi",

  // Tenant rent distribution pie/donut - from CanvasTopTenancies query
  TENANT_RENT_DISTRIBUTION: "asset.tenantRentDistribution",

  // Arrears (restancer) KPIs - from CanvasAssetAging (single assetAging call);
  // the per-lease table below still uses the CanvasArrears leaseAging fan-out.
  ARREARS_TOTAL_KPI: "asset.arrearsTotalKpi",
  ARREARS_CURRENT_KPI: "asset.arrearsCurrentKpi",
  ARREARS_DUE_TOTAL_KPI: "asset.arrearsDueTotalKpi",
  ARREARS_DUE_0_30_KPI: "asset.arrearsDue0To30Kpi",
  ARREARS_DUE_31_60_KPI: "asset.arrearsDue31To60Kpi",
  ARREARS_DUE_61_90_KPI: "asset.arrearsDue61To90Kpi",
  ARREARS_DUE_90_PLUS_KPI: "asset.arrearsDue90PlusKpi",
  ARREARS_BY_LEASE: "asset.arrearsByLease",

  // Rent-at-lease-start scatter - tenancies list + bundled leaseRent fan-out
  RENT_AT_LEASE_START_SCATTER: "asset.rentAtLeaseStartScatter",

  // Vacant tenancies table - reuses CanvasTopTenancies data, filters to ghost-tenant tenancies
  VACANT_TENANCIES_TABLE: "asset.vacantTenanciesTable",

  // New lettings (last 12m) distribution by typeGroup - from CanvasTopTenancies
  NEW_LETTINGS_BY_TYPE_GROUP: "asset.newLettingsByTypeGroup",

  // Break options table - from dedicated CanvasBreakOptions query
  BREAK_OPTIONS_TABLE: "asset.breakOptionsTable",

  // Area vs economic vacancy over time - combined chart from AssetBreakdownBetween
  VACANCY_RATE_AREA_VS_ECONOMIC: "asset.vacancyRateAreaVsEconomic",
} as const;

registerSourceMetadata({
  [AssetSources.TOTAL_UNITS]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total number of units in the asset. Accepts optional typeGroups filter/variable to aggregate only matching unit-type segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TOTAL_UNITS",
  },
  [AssetSources.ANNUAL_RENT]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total annual rent for the asset. Accepts optional typeGroups filter/variable to aggregate only matching unit-type segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ANNUAL_RENT",
  },
  [AssetSources.LEASABLE_AREA]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total leasable area in square meters. Accepts optional typeGroups filter/variable to aggregate only matching unit-type segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_LEASABLE_AREA",
  },
  [AssetSources.RENT_PER_SQM]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Annual rent per square meter. Accepts optional typeGroups filter to aggregate matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENT_PER_SQM",
  },
  [AssetSources.VACANCY_RATE_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Physical vacancy rate as a single KPI (vacantCount / totalCount * 100) from AssetBreakdownAt. Accepts optional typeGroups filter to aggregate matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANCY_RATE_KPI",
  },
  [AssetSources.ECONOMIC_VACANCY_RATE_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Economic vacancy rate as a single KPI (rents[0].idleRate * 100) from AssetBreakdownAt. Shows the share of potential rent lost to vacancy. Accepts optional typeGroups filter to aggregate matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ECONOMIC_VACANCY_RATE_KPI",
  },
  [AssetSources.AREA_VACANCY_RATE_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Area-based vacancy rate as a single KPI (areas[0].idleRate * 100) from AssetBreakdownAt. Shows the share of leasable area currently vacant. Accepts optional typeGroups filter to aggregate matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_AREA_VACANCY_RATE_KPI",
  },
  [AssetSources.BUSINESS_PLAN_PRICE]: {
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Business plan price",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_BUSINESS_PLAN_PRICE",
  },
  [AssetSources.BUSINESS_PLAN_PRICE_SQM]: {
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Business plan price per square meter",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_BUSINESS_PLAN_PRICE_SQM",
  },
  [AssetSources.ALLOCATED_PURCHASE_PRICE]: {
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Allocated purchase price",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ALLOCATED_PURCHASE_PRICE",
  },
  [AssetSources.APP_COST]: {
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "APP plus additional costs",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_APP_COST",
  },
  [AssetSources.RENT_BY_TYPE_GROUP]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Rent development by typeGroup (from AssetBreakdownBetween). Shows Total + breakdown by Residential, Commercial, etc. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional — scope to specific unit types), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional — slices display to a sub-range of the fetched data).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENT_BY_TYPE_GROUP",
  },
  [AssetSources.VACANCY_RATE]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Vacancy rate over time by typeGroup (from AssetBreakdownBetween). Calculated as vacantCount/totalCount. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANCY_RATE",
  },
  [AssetSources.VACANCY_RATE_BY_AREA]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description:
      "Vacancy rate over time weighted by area (from AssetBreakdownBetween). Uses areas[0].idleRate from the API directly — more accurate than the per-unit rate when unit sizes vary significantly. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANCY_RATE_BY_AREA",
  },
  [AssetSources.IDLE_TENANCY_RATE]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Idle tenancy rate (economic) by typeGroup (from AssetBreakdownBetween). Shows % of potential rent lost to vacancy. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_IDLE_TENANCY_RATE",
  },
  [AssetSources.IDLE_ANNUAL_AMOUNT]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Idle annual rent amount (churn) by typeGroup (from AssetBreakdownBetween). Shows absolute DKK lost to vacancy. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_IDLE_ANNUAL_AMOUNT",
  },
  [AssetSources.RENT_PER_SQM_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Annual rent per square meter over time by typeGroup (from AssetBreakdownBetween). Calculated as BASE_RENT / leasable area per period. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENT_PER_SQM_OVER_TIME",
  },
  [AssetSources.RENT_DEVELOPMENT_YOY]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Rental income year-over-year percentage change. Derived from breakdownBetween data with date range automatically extended 1 year back. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required — drives the displayed range), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional display slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENT_DEVELOPMENT_YOY",
  },
  [AssetSources.UNIT_TYPE_BY_AREA]: {
    dataType: "chart-sunburst",
    queryGroup: "asset.propertyTenancies",
    description: "Unit type distribution by area — two-level sunburst: typeGroup > tenancyType (from assetManagementTenancies)",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_UNIT_TYPE_BY_AREA",
  },
  [AssetSources.UNIT_TYPE_BY_COUNT]: {
    dataType: "chart-sunburst",
    queryGroup: "asset.propertyTenancies",
    description: "Unit type distribution by count — two-level sunburst: typeGroup > tenancyType (from assetManagementTenancies)",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_UNIT_TYPE_BY_COUNT",
  },
  [AssetSources.BREAKDOWNS_LIST]: {
    dataType: "table",
    queryGroup: "asset.breakdownsList",
    description: "List of asset breakdowns (properties or companies) with KPIs for table display. Canvas-level filters (set via canvas variables or FILTER_DROPDOWN widgets): assetType ('PROPERTY'|'COMPANY', default 'PROPERTY'), groupIds (string[], optional — scope to specific groups), page (number, default 1), pageSize (number, default 100 — controls query page size), sort ({ key: AssetBreakdownSortKey, order: 'ASC'|'DESC' }). Widget-level variables also accept page/pageSize for client-side slicing within the fetched result.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_BREAKDOWNS_LIST",
  },
  [AssetSources.TOTAL_RETURN_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description: "Total return over time (Totalafkast over tid). Waterfall chart with direct and indirect yield grouped by years. Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), dateRange widget variable (optional — slices display; NOT a canvas-level filter widget).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TOTAL_RETURN_OVER_TIME",
  },
  [AssetSources.TOTAL_RETURN_PERCENT_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description: "Total return % period-over-period. Bar chart showing direct yield % and indirect yield % for each period. Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), dateRange widget variable (optional — slices display; NOT a canvas-level filter widget).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TOTAL_RETURN_PERCENT_OVER_TIME",
  },
  [AssetSources.TOTAL_RETURN_PERCENT_WATERFALL]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description:
      "Total return % waterfall from a static baseline date. Direct yield % = NOI / baseline PV, Indirect yield % = PV change / baseline PV. Cumulative return since baseline. Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), baselineDate widget variable (ISO date string — fixes the baseline; omit to use the earliest available data point), dateRange widget variable (optional display slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TOTAL_RETURN_PERCENT_WATERFALL",
  },
  [AssetSources.PROPERTY_VALUE_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.yield",
    description: "Property value (ejendomsværdi) — latest available quarterly snapshot from rawBalancesByCategoryType(property_value).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_PROPERTY_VALUE_KPI",
  },
  [AssetSources.PROPERTY_VALUE_LAST_QUARTER_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.yield",
    description: "Property value (ejendomsværdi) — value at the last concluded quarter (the full quarter before today).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_PROPERTY_VALUE_LAST_QUARTER_KPI",
  },
  [AssetSources.OPERATING_MARGIN_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "Operating margin over time. Bar chart showing NOI/Revenue percentage grouped by years. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings OR widget variables.dateRange — both work), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_OPERATING_MARGIN_OVER_TIME",
  },
  [AssetSources.NET_OPERATING_INCOME_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "Net Operating Income (NOI) over time as an absolute amount. Bar chart grouped by years. Uses same data as operating margin. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings OR widget variables.dateRange — both work), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_NET_OPERATING_INCOME_OVER_TIME",
  },
  [AssetSources.CHURN_RATE]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Tenant churn rate over time by typeGroup (from AssetBreakdownBetween). Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional — preferred way to scope by unit type), residential widget variable (boolean shorthand — if true forces typeGroups=['RESIDENTIAL']; use typeGroups for multi-type), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional display slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_CHURN_RATE",
  },
  [AssetSources.TERMINATIONS_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.terminations",
    description: "Terminations (opsigelser) over time — count of termination notices per period by typeGroup (from the tenancies endpoint, lease.terminationNoticeReceivedAt).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TERMINATIONS_OVER_TIME",
  },
  [AssetSources.WAULT]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Weighted Average Unexpired Lease Term (WAULT) over time by typeGroup (from AssetBreakdownBetween). Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional — preferred way to scope by unit type), commercial widget variable (boolean shorthand — if true forces typeGroups=['COMMERCIAL']; use typeGroups for multi-type), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional display slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_WAULT",
  },
  [AssetSources.WAULT_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdownBetween",
    description: "Latest WAULT value as a single KPI metric (from AssetBreakdownBetween). Returns the most recent value in the date range as a key-value with percentage change vs previous period. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional — preferred way to scope by unit type), commercial widget variable (boolean shorthand — if true forces typeGroups=['COMMERCIAL']; use typeGroups for multi-type).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_WAULT_KPI",
  },
  [AssetSources.CHURN_ROLLING_12M_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Annual churn rate based on rolling 12 months as a single KPI metric (from AssetBreakdownAt point-in-time). Accepts optional typeGroups filter to average matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_CHURN_ROLLING_12M_KPI",
  },
  [AssetSources.RETURN_ACTUAL_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Actual total return (NOI + property value change) for a widget-configured period. Defaults to last completed calendar year. Period is set via widget variables.dateRange OR filterBindings (either mechanism works). IMPORTANT: does NOT read the canvas-level dateRange filter widget directly — must be bound via variables.dateRange or a filterBindings entry. timeRange variable controls the period grain (default 'yearly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RETURN_ACTUAL_KPI",
  },
  [AssetSources.RETURN_BUDGETED_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.budgetedReturn",
    description:
      "Budgeted return (NOI from default budget) for a widget-configured period. Defaults to current calendar year. Period is set via widget variables.dateRange OR filterBindings (either mechanism works). IMPORTANT: does NOT read the canvas-level dateRange filter widget directly.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RETURN_BUDGETED_KPI",
  },
  [AssetSources.DIRECT_YIELD_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Direct yield % KPI — NOI / beginning-of-period property value × 100. Period is set via widget variables.dateRange OR filterBindings (either mechanism works); defaults to last completed calendar year. timeRange variable controls the period grain (default 'yearly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_DIRECT_YIELD_KPI",
  },
  [AssetSources.INDIRECT_YIELD_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Indirect yield % KPI — property value change / beginning-of-period property value × 100. Period is set via widget variables.dateRange OR filterBindings (either mechanism works); defaults to last completed calendar year. timeRange variable controls the period grain (default 'yearly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_INDIRECT_YIELD_KPI",
  },
  [AssetSources.IRR_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.irr",
    description:
      "Historical IRR (Internal Rate of Return) from acquisition date to selected period. Uses property value as GAV and income statement cash flows. Accepts: variables.categoryId (string — ID of the income-statement category whose cash flows to use; the query fetches all income statements and filters client-side), dateRange filter/variable { from, to } — overrides default period (from = acquisition date, to = today).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_IRR_KPI",
  },
});

// ============================================================================
// MULTI-ASSET SOURCES (assets.* — portfolio-level aggregations)
// ============================================================================

/**
 * Source keys for multi-asset (portfolio-level) data.
 * These aggregate across all assets matching the given groupIds.
 * Contrast with AssetSources (asset.*) which operate on a single asset.
 */
export const AssetsSources = {
  /** Rolling 12-month churn rate aggregated across multiple assets (weighted average by unit count) */
  CHURN_RATE_ROLLING_12M: "assets.churnRateRolling12Months",
  /** Rolling 12-month SUM of monthly churn rates (churnRolling1MRate summed over trailing 12 months) */
  CHURN_RATE_ROLLING_12M_SUM: "assets.churnRateRolling12MonthsSum",
  /** Monthly churn rate aggregated across multiple assets (weighted average by unit count) */
  CHURN_RATE_MONTHLY: "assets.churnRateMonthly",
  /** Physical vacancy rate (vacant units / total units) pooled across multiple assets per month */
  VACANCY_RATE: "assets.vacancyRate",
  /** Nominal (economic) vacancy rate (idle annual rent / total annual rent) pooled across multiple assets per month */
  VACANCY_RATE_NOMINEL: "assets.vacancyRateNominel",
  /** Total annual rent pooled across multiple assets per month */
  ACTUAL_RENT: "assets.actualRent",
  /** Annual rent per square meter pooled across multiple assets per month */
  SQUARE_METER_RENT: "assets.squareMeterRent",
} as const;

registerSourceMetadata({
  [AssetsSources.CHURN_RATE_ROLLING_12M]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.churnBetween",
    description: "Backend-computed rolling 12-month churn rate (churnRolling12MRate) averaged across multiple assets. Canvas variables: groupIds (string | string[], optional — scope to asset group), dateFrom / dateTo (ISO strings, optional — default 2 years ago to today; expanded by any FILTER_DATE_RANGE widget dateRange). Widget filterBindings: assetIds (string[], optional — narrow to specific assets within the group).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_CHURN_RATE_ROLLING_12M",
  },
  [AssetsSources.CHURN_RATE_ROLLING_12M_SUM]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.churnBetween",
    description:
      "Rolling 12-month SUM of monthly churn rates (churnRolling1MRate summed over trailing 12 months) across multiple assets. Same canvas variables as assets.churnRateRolling12Months: groupIds, dateFrom/dateTo, assetIds filterBinding.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_CHURN_RATE_ROLLING_12M_SUM",
  },
  [AssetsSources.CHURN_RATE_MONTHLY]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.churnBetween",
    description: "Monthly tenant churn rate observations aggregated across multiple assets (weighted average by unit count). Same canvas variables as assets.churnRateRolling12Months: groupIds, dateFrom/dateTo, assetIds filterBinding.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_CHURN_RATE_MONTHLY",
  },
  [AssetsSources.VACANCY_RATE]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.vacancyBetween",
    description: "Physical vacancy rate (vacantCount / totalCount) pooled across multiple assets per month. Canvas variables: groupIds (string | string[], optional), dateFrom / dateTo (ISO strings, optional — default 2 years ago to today). Widget filterBindings: assetIds (string[], optional).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_VACANCY_RATE",
  },
  [AssetsSources.VACANCY_RATE_NOMINEL]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.vacancyBetween",
    description: "Absolute annual currency amount lost to vacancy (idleAnnualAmount) pooled across multiple assets per month. Same canvas variables as assets.vacancyRate: groupIds, dateFrom/dateTo, assetIds filterBinding.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_VACANCY_RATE_NOMINEL",
  },
  [AssetsSources.ACTUAL_RENT]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.vacancyBetween",
    description: "Total annual rent pooled across multiple assets per month. Same canvas variables as assets.vacancyRate: groupIds, dateFrom/dateTo, assetIds filterBinding.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_ACTUAL_RENT",
  },
  [AssetsSources.SQUARE_METER_RENT]: {
    dataType: "chart-timeseries",
    queryGroup: "assets.vacancyBetween",
    description: "Annual rent per square meter pooled across multiple assets per month. Same canvas variables as assets.vacancyRate: groupIds, dateFrom/dateTo, assetIds filterBinding.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSETS_SQUARE_METER_RENT",
  },
  [AssetSources.TOP_TENANCIES_BY_RENT]: {
    dataType: "table",
    queryGroup: "asset.topTenancies",
    description: "Top tenancies sorted by yearly rent descending (server-sorted, top 5). Columns: name, tenancyType, area (sqm), yearlyRent, status. Does NOT support typeGroups filtering — the query returns raw tenancyType values sorted server-side, so client-side typeGroup mapping would silently return fewer than the true top-N per type. Use asset.tenantRentDistribution or asset.vacantTenanciesTable for typeGroup-scoped views.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TOP_TENANCIES_BY_RENT",
  },
  [AssetSources.RENTED_UNITS_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Number of rented (occupied) units. Accepts optional typeGroups filter.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENTED_UNITS_KPI",
  },
  [AssetSources.VACANT_UNITS_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Number of vacant units. Accepts optional typeGroups filter.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANT_UNITS_KPI",
  },
  [AssetSources.RENTED_AREA_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Rented (occupied) area in square meters. Accepts optional typeGroups filter.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENTED_AREA_KPI",
  },
  [AssetSources.VACANT_AREA_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Vacant area in square meters. Accepts optional typeGroups filter.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANT_AREA_KPI",
  },
  [AssetSources.VACANCY_AMOUNT_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Vacancy amount (DKK lost to vacancy annually) from AssetBreakdownAt. Uses BASE_RENT idleAnnualAmount. Accepts optional typeGroups filter to aggregate matching segments.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANCY_AMOUNT_KPI",
  },
  [AssetSources.RENTAL_INCOME_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description:
      "Gross rental income (bruttolejeindtægt, GRI) for the latest available period within the selected " +
      "date range, at the chosen granularity (month/quarter/year). Based on the property's accounting " +
      "(income-statement) data; picks the most recent period in range that has booked rental income. " +
      "Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), dateRange (canvas-level filter widget OR widget variables.dateRange).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENTAL_INCOME_KPI",
  },
  [AssetSources.NOI_MARGIN_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description: "NOI margin (NOI / GRI × 100) for the latest period in the selected range as a single KPI percentage. Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), dateRange (canvas-level filter widget OR widget variables.dateRange).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_NOI_MARGIN_KPI",
  },
  [AssetSources.RESULTS_MARGIN_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description:
      "Results margin (full result / GRI × 100) for the latest period in the selected range as a single KPI percentage. Full result is the sum of all income-aggregated categories. " +
      "Accepts: timeRange variable ('monthly'|'quarterly'|'yearly', default 'yearly'), dateRange (canvas-level filter widget OR widget variables.dateRange).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RESULTS_MARGIN_KPI",
  },
  [AssetSources.INCOME_NOMINAL_OVER_TIME]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "Combined nominal time series showing rental income (GRI), NOI, and full result as separate series over time. Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings OR widget variables.dateRange — both work), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly').",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_INCOME_NOMINAL_OVER_TIME",
  },
  [AssetSources.INCOME_BY_CATEGORY]: {
    dataType: "table",
    queryGroup: "asset.incomeByCategory",
    description:
      "Per-property income table: one row per income category (GRI, NOI, Result) with columns realized, budget, realizedPrev. " +
      "NOT the same as financial.incomeByCategory (which is a subcategory bar chart for org-level reports). " +
      "Date range: uses widget variables.dateRange { from, to } if set — otherwise falls back to canvas-level dateRange filter widget — " +
      "otherwise defaults to YTD (Jan 1 → end of last completed month). " +
      "realizedPrev uses the same window offset by −1 year. 'Result' is the full P&L subtree.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_INCOME_BY_CATEGORY",
  },
  [AssetSources.RENT_GROWTH_ROLLING_12M_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.breakdownBetween",
    description:
      "Rent growth (rolling 12 months) as a percentage KPI. Compares the latest monthly BASE_RENT to 12 months earlier. " +
      "Accepts optional typeGroups filter to aggregate matching segments. " +
      "Returns null when less than 13 months of data are available.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_RENT_GROWTH_ROLLING_12M_KPI",
  },
  [AssetSources.TENANT_RENT_DISTRIBUTION]: {
    dataType: "chart-simple",
    queryGroup: "asset.topTenancies",
    description:
      "Tenant rent distribution pie/donut chart. Aggregates rent per tenant (summed across their tenancies); " +
      "excludes ghost/vacant tenants. Top 10 tenants by share of total annual contract rent (%), rest grouped as 'Øvrige'. " +
      "Accepts: typeGroups filter/variable (optional — RESIDENTIAL | COMMERCIAL | OTHER).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_TENANT_RENT_DISTRIBUTION",
  },
  [AssetSources.ARREARS_TOTAL_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Total arrears (balance) for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_TOTAL_KPI",
  },
  [AssetSources.ARREARS_CURRENT_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Current (not-yet-due) arrears for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_CURRENT_KPI",
  },
  [AssetSources.ARREARS_DUE_TOTAL_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description:
      "Total overdue arrears for the property — the sum of the aged buckets " +
      "(0–30 + 31–60 + 61–90 + 90+ days), EXCLUDING the not-yet-due 'current' bucket.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_DUE_TOTAL_KPI",
  },
  [AssetSources.ARREARS_DUE_0_30_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 0–30 days for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_DUE_0_30_KPI",
  },
  [AssetSources.ARREARS_DUE_31_60_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 31–60 days for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_DUE_31_60_KPI",
  },
  [AssetSources.ARREARS_DUE_61_90_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 61–90 days for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_DUE_61_90_KPI",
  },
  [AssetSources.ARREARS_DUE_90_PLUS_KPI]: {
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 90+ days for the property.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_DUE_90_PLUS_KPI",
  },
  [AssetSources.ARREARS_BY_LEASE]: {
    dataType: "table",
    queryGroup: "asset.arrears",
    description:
      "Top 10 leases by arrears (balance) descending. Columns: tenant, typeGroup, evictionStatus (EVICTION | NONE), " +
      "dueBand (oldest non-zero bucket: 90+ | 61-90 | 31-60 | 0-30 | CURRENT | NONE), amount. " +
      "Accepts: typeGroups filter/variable (optional — RESIDENTIAL | COMMERCIAL | OTHER), " +
      'excludeDueBands filter/variable (string[], optional — removes rows matching those dueBand values, e.g. ["CURRENT"] to hide not-yet-due leases).',  
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_ARREARS_BY_LEASE",
  },
  [AssetSources.RENT_AT_LEASE_START_SCATTER]: {
    dataType: "chart-scatter",
    queryGroup: "asset.rentAtLeaseStart",
    description:
      "Scatter of rent at lease start: one point per lease, x = lease start date, y = gross annual rent at " +
      "that start, series = typeGroup. Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
  },
  [AssetSources.VACANT_TENANCIES_TABLE]: {
    dataType: "table",
    queryGroup: "asset.topTenancies",
    description:
      "Vacant tenancies table — one row per tenancy that is vacant at the query date " +
      "(no current tenant or a ghost-tenant placeholder). Top 10 by potential annual rent. " +
      "Columns: identifier, typeGroup, area, annualRent, status. " +
      "Accepts: typeGroups filter/variable (optional — RESIDENTIAL | COMMERCIAL | OTHER).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANT_TENANCIES_TABLE",
  },
  [AssetSources.BREAK_OPTIONS_TABLE]: {
    dataType: "table",
    queryGroup: "asset.breakOptions",
    description:
      "Tenancies whose next break opportunity falls within a configurable horizon (default 12 months; " +
      "override via variables.horizonMonths). Which side counts as a break is controlled by " +
      "variables.breakSide: 'both' (default — earliest of landlord and tenant), 'landlord' " +
      "(only irrevocableUntilLandlord), or 'tenant' (only irrevocableUntilTenant). " +
      "Sorted by next-break date ascending. Columns: identifier, typeGroup, area, annualRent, " +
      "status (Udlejer | Lejer | Begge), nextBreakDate. " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_BREAK_OPTIONS_TABLE",
  },
  [AssetSources.VACANCY_RATE_AREA_VS_ECONOMIC]: {
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description:
      "Area vs economic vacancy over time as two series on one chart. " +
      "Areal-tomgang % = idle area / total area. " +
      "Økonomisk tomgang % = idle BASE_RENT / (contract + idle BASE_RENT). " +
      "Accepts: dateRange (canvas-level FILTER_DATE_RANGE widget via filterBindings, required), typeGroups filter/variable (optional), timeRange variable ('monthly'|'quarterly'|'yearly', default 'monthly'), periodSelection variable ('START_OF_PERIOD'|'END_OF_PERIOD', default 'END_OF_PERIOD'), dateRange widget variable (optional display slice).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_VACANCY_RATE_AREA_VS_ECONOMIC",
  },
  [AssetSources.NEW_LETTINGS_BY_TYPE_GROUP]: {
    dataType: "chart-simple",
    queryGroup: "asset.topTenancies",
    description:
      "New lettings in the last N months distributed by usage type (typeGroup). " +
      "Counts tenancies whose current (non-ghost) tenant moved in within the configurable horizon " +
      "(default 12 months; override via variables.horizonMonths). " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_NEW_LETTINGS_BY_TYPE_GROUP",
  },
});
